import type { DayLabel, DayVacation, Zone } from '../types';
import { saintFor } from './saints';
import { buildLabels, isoKey } from './holidays';
import { isoWeek } from './weeks';

const MONTH_NAMES = [
  'JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
  'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE',
];

const WEEKDAY_LETTERS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const ZONE_ORDER: Zone[] = ['A', 'B', 'C'];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function vacationBar(zones: Set<Zone>): HTMLElement {
  const wrap = el('span', 'vacBar');
  for (const z of ZONE_ORDER) {
    const active = zones.has(z);
    const stripe = el('span', active ? `vacStripe vacZone-${z}` : 'vacStripe');
    if (active) stripe.title = `Zone ${z}`;
    wrap.appendChild(stripe);
  }
  return wrap;
}

function renderMonth(
  year: number,
  month: number,
  labels: Map<string, DayLabel>,
  vacations: Map<string, DayVacation>,
  today: Date,
  showVacations: boolean,
): HTMLElement {
  const tbl = el('table', 'monthPanel');
  const colgroup = el('colgroup');
  colgroup.appendChild(el('col', 'colNum'));
  colgroup.appendChild(el('col', 'colLetter'));
  colgroup.appendChild(el('col', 'colLabel'));
  colgroup.appendChild(el('col', 'colRight'));
  tbl.appendChild(colgroup);
  const tbody = el('tbody');

  const head = el('tr');
  const th = el('td', 'monthTitle');
  th.colSpan = 4;
  th.textContent = MONTH_NAMES[month] ?? '';
  head.appendChild(th);
  tbody.appendChild(head);

  const lastDay = new Date(year, month + 1, 0).getDate();
  const todayIsThisMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(year, month, day);
    const dow = date.getDay();
    const key = isoKey(year, month, day);
    const label = labels.get(key);
    const vac = showVacations ? vacations.get(key) : undefined;

    let cls = 'day';
    let labelText: string;
    if (label && label.kind === 'holiday') {
      cls += ' dayHoliday';
      labelText = label.text;
    } else {
      labelText = label ? label.text : saintFor(month, day);
      if (dow === 0) cls += ' daySunday';
      else if (dow === 6) cls += ' daySaturday';
    }
    if (todayIsThisMonth && day === todayDate) {
      cls = 'day dayToday';
    }

    const tr = el('tr', cls);
    tr.appendChild(el('td', 'dayNum', String(day)));
    tr.appendChild(el('td', 'dayLetter', WEEKDAY_LETTERS[dow] ?? ''));
    tr.appendChild(el('td', 'dayLabel', labelText));

    const right = el('td', 'dayRight');
    if (vac && vac.zones.size > 0) {
      right.appendChild(vacationBar(vac.zones));
    } else if (dow === 1) {
      right.appendChild(el('span', 'weekNum', String(isoWeek(date))));
    }
    tr.appendChild(right);

    tbody.appendChild(tr);
  }

  // Pad to 31 rows so all month panels have equal height.
  for (let day = lastDay; day < 31; day++) {
    const tr = el('tr', 'day dayPad');
    const td = el('td');
    td.colSpan = 4;
    td.innerHTML = '&nbsp;';
    tr.appendChild(td);
    tbody.appendChild(tr);
  }

  tbl.appendChild(tbody);
  return tbl;
}

export interface RenderOptions {
  year: number;
  semester: 0 | 1;
  showVacations: boolean;
  vacations: Map<string, DayVacation>;
  today?: Date;
}

export function renderSemester(opts: RenderOptions): HTMLElement {
  const today = opts.today ?? new Date();
  const labels = buildLabels(opts.year);
  const grid = el('div', 'calendarGrid');
  grid.dataset.semester = String(opts.semester);
  for (let i = 0; i < 6; i++) {
    const month = opts.semester * 6 + i;
    grid.appendChild(renderMonth(opts.year, month, labels, opts.vacations, today, opts.showVacations));
  }
  return grid;
}
